import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Send, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from './markdown';
import type { CreatorMessage, StructuredProgress } from './types';

interface Props {
  messages: CreatorMessage[];
  isLoading: boolean;
  progress: StructuredProgress | null;
  error: string | null;
  onSend: (text: string) => void;
}

const SUGGESTION =
  'e.g. "I want a screener to find frequent oat-milk buyers for a packaging study in the US."';

const ChatPanel: React.FC<Props> = ({ messages, isLoading, progress, error, onSend }) => {
  const [input, setInput] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const submit = () => {
    const text = input.trim();
    if (!text || isLoading) return;
    onSend(text);
    setInput('');
  };

  return (
    <div className="flex flex-col h-full bg-white min-w-0">
      <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
        {messages.length === 0 && !isLoading && (
          <div className="h-full flex flex-col items-center justify-center text-center px-6">
            <div className="w-12 h-12 rounded-2xl bg-[#07E5D1]/10 flex items-center justify-center mb-3">
              <Sparkles className="w-6 h-6 text-[#07E5D1]" />
            </div>
            <h2 className="text-base font-semibold text-[#1C1C1E] mb-1">Let's build a survey</h2>
            <p className="text-sm text-gray-500 max-w-sm">
              Tell me what decision you're trying to make and who you want to hear from. I'll ask a
              few questions, then draft a survey you can refine.
            </p>
            <p className="text-xs text-gray-400 mt-3 max-w-sm">{SUGGESTION}</p>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            <div
              className={
                m.role === 'user'
                  ? 'max-w-[85%] bg-[#07E5D1] text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm'
                  : 'max-w-[90%] bg-[#FAFBFC] border border-[#F1F5F9] rounded-2xl rounded-bl-md px-4 py-2.5'
              }
            >
              {m.role === 'user' ? (
                <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {m.content}
                </ReactMarkdown>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-[#FAFBFC] border border-[#F1F5F9] rounded-2xl rounded-bl-md px-4 py-2.5 flex items-center gap-2">
              <Loader2 className="w-4 h-4 text-[#07E5D1] animate-spin" />
              <span className="text-xs text-gray-500">{progress?.message || 'Thinking…'}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="border-t border-[#F1F5F9] p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="Describe your survey objective…"
            rows={1}
            className="flex-1 resize-none text-sm border border-[#E5E7EB] rounded-xl px-3 py-2.5 max-h-32 focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/40"
          />
          <button
            onClick={submit}
            disabled={!input.trim() || isLoading}
            className="p-2.5 rounded-xl bg-[#07E5D1] text-white hover:brightness-95 disabled:opacity-50 transition"
            aria-label="Send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
